import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorType } from '../../api/errorHandling';
import {
 ForgotPasswordDTO,
 JWTRefreshTokenSubmitDTO,
 JWTTokenDTO,
 LoginTwoFAVerifyDTO,
 ResetPasswordDTO,
 TwoFAVerifyDTO,
 VerifyDTO,
} from './identityTypes';
import {
 commonReducers,
 CommonState,
 initialCommonState,
} from '../_common/commonReducers';

interface IdentityState extends CommonState {
 qrCode?: { uri: string } | null;
 error?: { [key: string]: ErrorType | null };
 login?: JWTTokenDTO | null;
 uri?: string | null;
}

export const initialState: IdentityState = {
 ...initialCommonState,
 login: null,
 qrCode: null,
 error: {},
 uri: null,
};

const identitySlice = createSlice({
 name: 'identity',
 initialState,
 reducers: {
  ...commonReducers,
  identityLoginStart(
   state,
   action: PayloadAction<{
    email: string;
    password: string;
    connectionKey?: number | null;
   }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityLogin: true },
   };
  },
  identityLoginSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityLogin: true },
    loading: { ...state.loading, identityLogin: null },
    error: { ...(state.error || {}), identityLogin: null },
   };
  },
  identityLoginFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityLogin: null },
    loading: { ...state.loading, identityLogin: null },
    error: { ...(state.error || {}), identityLogin: action.payload },
   };
  },
  identityLogoutStart(state) {
   return {
    ...state,
    loading: { ...state.loading, identityLogout: true },
   };
  },
  identityLogoutSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, identityLogout: true },
    loading: { ...state.loading, identityLogout: null },
   };
  },
  identityLogoutFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityLogout: null },
    loading: { ...state.loading, identityLogout: null },
    error: { ...(state.error || {}), identityLogout: action.payload },
   };
  },
  identityRefreshTokenStart(
   state,
   action: PayloadAction<JWTRefreshTokenSubmitDTO>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityRefreshToken: true },
   };
  },
  identityRefreshTokenSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityRefreshToken: true },
    loading: { ...state.loading, identityRefreshToken: null },
    error: { ...(state.error || {}), identityRefreshToken: null },
   };
  },
  identityRefreshTokenFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityRefreshToken: null },
    loading: { ...state.loading, identityRefreshToken: null },
    error: { ...(state.error || {}), identityRefreshToken: action.payload },
   };
  },
  identityMicrosoftStart(
   state,
   action: PayloadAction<{ redirectUri?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityMicrosoft: true },
   };
  },
  identityMicrosoftSuccess(state, action: PayloadAction<{ uri: string }>) {
   return {
    ...state,
    uri: action.payload.uri,
    success: { ...state.success, identityMicrosoft: true },
    loading: { ...state.loading, identityMicrosoft: null },
    error: { ...(state.error || {}), identityMicrosoft: null },
   };
  },
  identityMicrosoftFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityMicrosoft: null },
    loading: { ...state.loading, identityMicrosoft: null },
    error: { ...(state.error || {}), identityMicrosoft: action.payload },
   };
  },
  identityMicrosoftCallbackStart(
   state,
   action: PayloadAction<{ code?: string; redirectUri?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityMicrosoftCallback: true },
   };
  },
  identityMicrosoftCallbackSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityMicrosoftCallback: true },
    loading: { ...state.loading, identityMicrosoftCallback: null },
    error: { ...(state.error || {}), identityMicrosoftCallback: null },
   };
  },
  identityMicrosoftCallbackFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityMicrosoftCallback: null },
    loading: { ...state.loading, identityMicrosoftCallback: null },
    error: {
     ...(state.error || {}),
     identityMicrosoftCallback: action.payload,
    },
   };
  },
  identityGoogleStart(state, action: PayloadAction<{ redirectUri?: string }>) {
   return {
    ...state,
    loading: { ...state.loading, identityGoogle: true },
   };
  },
  identityGoogleSuccess(state, action: PayloadAction<{ uri: string }>) {
   return {
    ...state,
    uri: action.payload.uri,
    success: { ...state.success, identityGoogle: true },
    loading: { ...state.loading, identityGoogle: null },
    error: { ...(state.error || {}), identityGoogle: null },
   };
  },
  identityGoogleFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGoogle: null },
    loading: { ...state.loading, identityGoogle: null },
    error: { ...(state.error || {}), identityGoogle: action.payload },
   };
  },
  identityGoogleCallbackStart(
   state,
   action: PayloadAction<{ code?: string; redirectUri?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityGoogleCallback: true },
   };
  },
  identityGoogleCallbackSuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityGoogleCallback: true },
    loading: { ...state.loading, identityGoogleCallback: null },
    error: { ...(state.error || {}), identityGoogleCallback: null },
   };
  },
  identityGoogleCallbackFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGoogleCallback: null },
    loading: { ...state.loading, identityGoogleCallback: null },
    error: { ...(state.error || {}), identityGoogleCallback: action.payload },
   };
  },
  identityVerify2FAStart(state, action: PayloadAction<LoginTwoFAVerifyDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityVerify2FA: true },
   };
  },
  identityVerify2FASuccess(state, action: PayloadAction<JWTTokenDTO>) {
   return {
    ...state,
    login: { ...state.login, ...action.payload },
    success: { ...state.success, identityVerify2FA: true },
    loading: { ...state.loading, identityVerify2FA: null },
    error: { ...(state.error || {}), identityVerify2FA: null },
   };
  },
  identityVerify2FAFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityVerify2FA: null },
    loading: { ...state.loading, identityVerify2FA: null },
    error: { ...(state.error || {}), identityVerify2FA: action.payload },
   };
  },
  identityForgotPasswordStart(state, action: PayloadAction<ForgotPasswordDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityForgotPassword: true },
   };
  },
  identityForgotPasswordSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identityForgotPassword: true },
    loading: { ...state.loading, identityForgotPassword: null },
    error: { ...(state.error || {}), identityForgotPassword: null },
   };
  },
  identityForgotPasswordFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityForgotPassword: null },
    loading: { ...state.loading, identityForgotPassword: null },
    error: { ...(state.error || {}), identityForgotPassword: action.payload },
   };
  },
  identityResetPasswordStart(state, action: PayloadAction<ResetPasswordDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityResetPassword: true },
   };
  },
  identityResetPasswordSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identityResetPassword: true },
    loading: { ...state.loading, identityResetPassword: null },
    error: { ...(state.error || {}), identityResetPassword: null },
   };
  },
  identityResetPasswordFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityResetPassword: null },
    loading: { ...state.loading, identityResetPassword: null },
    error: { ...(state.error || {}), identityResetPassword: action.payload },
   };
  },
  identityGenerate2FACodeStart(state, action: PayloadAction<TwoFAVerifyDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identityGenerate2FACode: true },
   };
  },
  identityGenerate2FACodeSuccess(
   state,
   action: PayloadAction<{ uri: string }>,
  ) {
   console.log(action.payload);
   return {
    ...state,
    qrCode: { uri: action.payload.uri },
    success: { ...state.success, identityGenerate2FACode: true },
    loading: { ...state.loading, identityGenerate2FACode: null },
    error: { ...(state.error || {}), identityGenerate2FACode: null },
   };
  },
  identityGenerate2FACodeFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityGenerate2FACode: null },
    loading: { ...state.loading, identityGenerate2FACode: null },
    error: { ...(state.error || {}), identityGenerate2FACode: action.payload },
   };
  },
  identitySetVerificationStart(state, action: PayloadAction<VerifyDTO>) {
   return {
    ...state,
    loading: { ...state.loading, identitySetVerification: true },
   };
  },
  identitySetVerificationSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identitySetVerification: true },
    loading: { ...state.loading, identitySetVerification: null },
    error: { ...(state.error || {}), identitySetVerification: null },
   };
  },
  identitySetVerificationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identitySetVerification: null },
    loading: { ...state.loading, identitySetVerification: null },
    error: { ...(state.error || {}), identitySetVerification: action.payload },
   };
  },
  identityConfirmVerificationStart(
   state,
   action: PayloadAction<{
    sessionId?: string;
    userid?: string;
    method?: 1 | 2 | 3;
    code?: string;
   }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityConfirmVerification: true },
   };
  },
  identityConfirmVerificationSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identityConfirmVerification: true },
    loading: { ...state.loading, identityConfirmVerification: null },
    error: { ...(state.error || {}), identityConfirmVerification: null },
   };
  },
  identityConfirmVerificationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityConfirmVerification: null },
    loading: { ...state.loading, identityConfirmVerification: null },
    error: {
     ...(state.error || {}),
     identityConfirmVerification: action.payload,
    },
   };
  },
  identitySetTotpStart(
   state,
   action: PayloadAction<{ userid?: string; sessionId?: string }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identitySetTotp: true },
   };
  },
  identitySetTotpSuccess(state, action: PayloadAction<{ uri: string }>) {
   return {
    ...state,
    qrCode: { uri: action.payload.uri },
    success: { ...state.success, identitySetTotp: true },
    loading: { ...state.loading, identitySetTotp: null },
    error: { ...(state.error || {}), identitySetTotp: null },
   };
  },
  identitySetTotpFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identitySetTotp: null },
    loading: { ...state.loading, identitySetTotp: null },
    error: { ...(state.error || {}), identitySetTotp: action.payload },
   };
  },
  identityVerifyTotpStart(
   state,
   action: PayloadAction<{
    userid?: string;
    sessionId?: string;
    code?: string;
   }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, identityVerifyTotp: true },
   };
  },
  identityVerifyTotpSuccess(state, action: PayloadAction<string>) {
   return {
    ...state,
    success: { ...state.success, identityVerifyTotp: true },
    loading: { ...state.loading, identityVerifyTotp: null },
    error: { ...(state.error || {}), identityVerifyTotp: null },
   };
  },
  identityVerifyTotpFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityVerifyTotp: null },
    loading: { ...state.loading, identityVerifyTotp: null },
    error: { ...(state.error || {}), identityVerifyTotp: action.payload },
   };
  },
  identityRevokeTokenStart(state, action: PayloadAction<{ Id: string }>) {
   return {
    ...state,
    loading: { ...state.loading, identityRevokeToken: true },
   };
  },
  identityRevokeTokenSuccess(state) {
   return {
    ...state,
    success: { ...state.success, identityRevokeToken: true },
    loading: { ...state.loading, identityRevokeToken: null },
    error: { ...(state.error || {}), identityRevokeToken: null },
   };
  },
  identityRevokeTokenFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityRevokeToken: null },
    loading: { ...state.loading, identityRevokeToken: null },
    error: { ...(state.error || {}), identityRevokeToken: action.payload },
   };
  },
  identityRevokeAllTokensStart(state) {
   return {
    ...state,
    loading: { ...state.loading, identityRevokeAllTokens: true },
   };
  },
  identityRevokeAllTokensSuccess(state) {
   return {
    ...state,
    success: { ...state.success, identityRevokeAllTokens: true },
    loading: { ...state.loading, identityRevokeAllTokens: null },
    error: { ...(state.error || {}), identityRevokeAllTokens: null },
   };
  },
  identityRevokeAllTokensFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, identityRevokeAllTokens: null },
    loading: { ...state.loading, identityRevokeAllTokens: null },
    error: { ...(state.error || {}), identityRevokeAllTokens: action.payload },
   };
  },
 },
});
export const {
 getTableStart,
 getTableSuccess,
 getTableFailure,
 getTableFinish,
 getItemStart,
 getItemSuccess,
 getItemFailure,
 getItemFinish,
 createItemStart,
 createItemSuccess,
 createItemFailure,
 createItemFinish,
 updateItemStart,
 updateItemSuccess,
 updateItemFailure,
 updateItemFinish,
 deleteItemsStart,
 deleteItemsSuccess,
 deleteItemsFailure,
 deleteItemsFinish,

 deleteItemStart,
 deleteItemSuccess,
 deleteItemFailure,
 deleteItemFinish,
 getTableStructureStart,
 getTableStructureSuccess,
 getTableStructureFinish,
 getTableStructureFailure,
 getItemDocumentsStart,
 getItemDocumentsSuccess,
 getItemDocumentsFailure,
 uploadItemDocumentsStart,
 uploadItemDocumentsSuccess,
 uploadItemDocumentsFailure,
 uploadItemDocumentsFinish,
 deleteItemDocumentStart,
 deleteItemDocumentSuccess,
 deleteItemDocumentFailure,
 deleteItemDocumentFinish,
 getTableExcelStart,
 getTableExcelSuccess,
 getTableExcelFailure,
 getTableExcelFinish,
 identityLoginStart,
 identityLoginSuccess,
 identityLoginFailure,
 identityLogoutStart,
 identityLogoutSuccess,
 identityLogoutFailure,
 identityRefreshTokenStart,
 identityRefreshTokenSuccess,
 identityRefreshTokenFailure,
 identityMicrosoftStart,
 identityMicrosoftSuccess,
 identityMicrosoftFailure,
 identityMicrosoftCallbackStart,
 identityMicrosoftCallbackSuccess,
 identityMicrosoftCallbackFailure,
 identityGoogleStart,
 identityGoogleSuccess,
 identityGoogleFailure,
 identityGoogleCallbackStart,
 identityGoogleCallbackSuccess,
 identityGoogleCallbackFailure,
 identityVerify2FAStart,
 identityVerify2FASuccess,
 identityVerify2FAFailure,
 identityForgotPasswordStart,
 identityForgotPasswordSuccess,
 identityForgotPasswordFailure,
 identityResetPasswordStart,
 identityResetPasswordSuccess,
 identityResetPasswordFailure,
 identityGenerate2FACodeStart,
 identityGenerate2FACodeSuccess,
 identityGenerate2FACodeFailure,
 identitySetVerificationStart,
 identitySetVerificationSuccess,
 identitySetVerificationFailure,
 identityConfirmVerificationStart,
 identityConfirmVerificationSuccess,
 identityConfirmVerificationFailure,
 identitySetTotpStart,
 identitySetTotpSuccess,
 identitySetTotpFailure,
 identityVerifyTotpStart,
 identityVerifyTotpSuccess,
 identityVerifyTotpFailure,
 identityRevokeTokenStart,
 identityRevokeTokenSuccess,
 identityRevokeTokenFailure,
 identityRevokeAllTokensStart,
 identityRevokeAllTokensSuccess,
 identityRevokeAllTokensFailure,
} = identitySlice.actions;
export default identitySlice;

